import { useState, useMemo } from 'react';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { generateSchemaSQL, generateMigrationSQL, DatabaseType } from '@/lib/sql-generator';
import {
  generateTypeScriptInterfaces,
  generateCompletePrismaSchema,
  generateCompleteDrizzleSchema,
  generateCompleteZodSchemas,
} from '@/lib/typescript-generator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  RiFileCopyLine,
  RiDownloadLine,
  RiCodeSLine,
  RiDatabase2Line,
  RiCheckLine,
  RiPlayLine,
  RiFileCodeLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const SqlPreviewPanel = () => {
  const { nodes, edges, isExecuting, executeSQL } = useSchemaDesignerStore();
  const [dbType, setDbType] = useState<DatabaseType>('postgres');
  const [copied, setCopied] = useState<string | null>(null);

  const schemaSQL = useMemo(
    () => generateSchemaSQL(nodes, edges, dbType),
    [nodes, edges, dbType]
  );

  const migrationSQL = useMemo(
    () => generateMigrationSQL(nodes, edges, dbType),
    [nodes, edges, dbType]
  );

  const typescriptInterfaces = useMemo(
    () => generateTypeScriptInterfaces(nodes),
    [nodes]
  );

  const prismaSchema = useMemo(
    () => generateCompletePrismaSchema(nodes, edges),
    [nodes, edges]
  );

  const drizzleSchema = useMemo(
    () => generateCompleteDrizzleSchema(nodes, dbType),
    [nodes, dbType]
  );

  const zodSchemas = useMemo(
    () => generateCompleteZodSchemas(nodes),
    [nodes]
  );

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
      console.error('Copy error:', error);
    }
  };

  const handleDownload = (content: string, filename: string) => {
    try {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('SQL file downloaded');
    } catch (error) {
      toast.error('Failed to download file');
      console.error('Download error:', error);
    }
  };

  const handleExecuteSQL = async () => {
    if (!schemaSQL || schemaSQL.startsWith('--')) {
      toast.error('No SQL to execute');
      return;
    }

    // TODO: Implement database connection integration
    toast.info('Coming soon! Connect a database first to execute SQL directly.', {
      duration: 4000,
    });

    // Placeholder for future implementation
    // try {
    //   await executeSQL(schemaSQL);
    //   toast.success('SQL executed successfully');
    // } catch (error) {
    //   toast.error('Failed to execute SQL');
    //   console.error('Execute error:', error);
    // }
  };

  const isEmpty = nodes.length === 0;

  return (
    <Card className="h-full flex flex-col border-border/60">
      <CardHeader className="pb-3 border-b border-border/40">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <RiCodeSLine className="h-5 w-5 text-primary" />
            SQL Preview
          </CardTitle>

          <div className="flex items-center gap-2">
            <Select value={dbType} onValueChange={(value) => setDbType(value as DatabaseType)}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="postgres">
                  <div className="flex items-center gap-2">
                    <RiDatabase2Line className="h-3 w-3" />
                    PostgreSQL
                  </div>
                </SelectItem>
                <SelectItem value="mysql">
                  <div className="flex items-center gap-2">
                    <RiDatabase2Line className="h-3 w-3" />
                    MySQL
                  </div>
                </SelectItem>
                <SelectItem value="sqlite">
                  <div className="flex items-center gap-2">
                    <RiDatabase2Line className="h-3 w-3" />
                    SQLite
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {!isEmpty && (
              <Badge variant="outline" className="text-xs px-2">
                {nodes.length} {nodes.length === 1 ? 'table' : 'tables'}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <Tabs defaultValue="schema" className="h-full flex flex-col">
          <div className="px-4 pt-3 border-b border-border/40">
            <TabsList className="grid w-full grid-cols-6 h-9">
              <TabsTrigger value="schema" className="text-xs">
                SQL
              </TabsTrigger>
              <TabsTrigger value="migration-up" className="text-xs">
                Migration
              </TabsTrigger>
              <TabsTrigger value="typescript" className="text-xs">
                TypeScript
              </TabsTrigger>
              <TabsTrigger value="prisma" className="text-xs">
                Prisma
              </TabsTrigger>
              <TabsTrigger value="drizzle" className="text-xs">
                Drizzle
              </TabsTrigger>
              <TabsTrigger value="zod" className="text-xs">
                Zod
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="schema" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={schemaSQL}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(schemaSQL, 'Schema SQL')}
              onDownload={() => handleDownload(schemaSQL, `schema-${dbType}-${Date.now()}.sql`)}
              onExecute={handleExecuteSQL}
              copied={copied === 'Schema SQL'}
              isExecuting={isExecuting}
            />
          </TabsContent>

          <TabsContent value="migration-up" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={migrationSQL.up}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(migrationSQL.up, 'Migration Up')}
              onDownload={() => handleDownload(migrationSQL.up, `migration-up-${Date.now()}.sql`)}
              copied={copied === 'Migration Up'}
            />
          </TabsContent>

          <TabsContent value="migration-down" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={migrationSQL.down}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(migrationSQL.down, 'Migration Down')}
              onDownload={() => handleDownload(migrationSQL.down, `migration-down-${Date.now()}.sql`)}
              copied={copied === 'Migration Down'}
            />
          </TabsContent>

          <TabsContent value="typescript" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={typescriptInterfaces}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(typescriptInterfaces, 'TypeScript')}
              onDownload={() => handleDownload(typescriptInterfaces, `types-${Date.now()}.ts`)}
              copied={copied === 'TypeScript'}
              language="typescript"
            />
          </TabsContent>

          <TabsContent value="prisma" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={prismaSchema}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(prismaSchema, 'Prisma Schema')}
              onDownload={() => handleDownload(prismaSchema, `schema-${Date.now()}.prisma`)}
              copied={copied === 'Prisma Schema'}
              language="prisma"
            />
          </TabsContent>

          <TabsContent value="drizzle" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={drizzleSchema}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(drizzleSchema, 'Drizzle Schema')}
              onDownload={() => handleDownload(drizzleSchema, `schema-${Date.now()}.ts`)}
              copied={copied === 'Drizzle Schema'}
              language="typescript"
            />
          </TabsContent>

          <TabsContent value="zod" className="flex-1 m-0 overflow-hidden">
            <SqlCodeBlock
              code={zodSchemas}
              isEmpty={isEmpty}
              onCopy={() => handleCopy(zodSchemas, 'Zod Schemas')}
              onDownload={() => handleDownload(zodSchemas, `validation-${Date.now()}.ts`)}
              copied={copied === 'Zod Schemas'}
              language="typescript"
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

interface SqlCodeBlockProps {
  code: string;
  isEmpty: boolean;
  onCopy: () => void;
  onDownload: () => void;
  onExecute?: () => void;
  copied: boolean;
  isExecuting?: boolean;
  language?: string;
}

const SqlCodeBlock = ({
  code,
  isEmpty,
  onCopy,
  onDownload,
  onExecute,
  copied,
  isExecuting,
}: SqlCodeBlockProps) => {
  return (
    <div className="h-full flex flex-col">
      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2 px-4 py-2 bg-muted/30 border-b border-border/40">
        {onExecute && !isEmpty && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExecute}
            disabled={isExecuting}
            className="h-7 px-3 gap-2 text-xs"
          >
            <RiPlayLine className="h-3 w-3" />
            {isExecuting ? 'Executing...' : 'Execute'}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onCopy}
          disabled={isEmpty}
          className="h-7 px-3 gap-2 text-xs"
        >
          {copied ? (
            <>
              <RiCheckLine className="h-3 w-3 text-green-600" />
              Copied
            </>
          ) : (
            <>
              <RiFileCopyLine className="h-3 w-3" />
              Copy
            </>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDownload}
          disabled={isEmpty}
          className="h-7 px-3 gap-2 text-xs"
        >
          <RiDownloadLine className="h-3 w-3" />
          Download
        </Button>
      </div>

      {/* Code Display */}
      <div className="flex-1 overflow-auto p-4 bg-muted/20">
        {isEmpty ? (
          <div className="flex items-center justify-center h-full text-center">
            <div className="space-y-2 text-sm text-muted-foreground">
              <RiCodeSLine className="h-8 w-8 mx-auto opacity-50" />
              <p>No tables defined yet</p>
              <p className="text-xs">Add tables to see generated SQL</p>
            </div>
          </div>
        ) : (
          <pre className="text-xs font-mono leading-relaxed text-foreground whitespace-pre-wrap break-words">
            {code}
          </pre>
        )}
      </div>
    </div>
  );
};
